import { useCallback, useMemo, useState } from 'react';
import { nextSortDirection, sortRows } from '../utils/tableSort';

/**
 * Client-side sorting for tables whose rows are already fully in memory —
 * the detail-page tabs, where the parent screen fetches the whole related list
 * and passes it down as a prop. (List screens sort server-side through URL
 * params instead; don't mix the two.)
 *
 * `accessors` maps a DataTable column `key` to the value that column sorts on.
 * It's explicit on purpose: a column's `key` very often isn't a row field
 * (`id` renders `debt.customId`, `option` renders `relWarranty.warranty.option`),
 * so `row[key]` would silently sort by `undefined`.
 *
 * `defaultColumn` is expected on every table: without it the rows keep the
 * API's order and no header shows an arrow, which reads as "unsorted" (e.g.
 * dues arriving date-desc while the user scans the № column). Its accessor
 * also breaks ties for every other column, unless `tieBreakerColumn` names a
 * different one (e.g. payments sorted by their due's Nro, where several
 * payments share a due and should read in date order).
 *
 * Returns props shaped for DataTable: `sortColumn`, `direction`, `onSort`.
 */
export function useClientSort(rows, { accessors = {}, defaultColumn = null, defaultDirection = 'asc', tieBreakerColumn = defaultColumn } = {}) {
	// Lazy initializer so the dev warning fires once per mount, not per render.
	const [sort, setSort] = useState(() => {
		if (process.env.NODE_ENV !== 'production' && !defaultColumn) {
			console.warn('useClientSort: pass a `defaultColumn` so the table always shows its active sort.');
		}
		return { column: defaultColumn, direction: defaultDirection };
	});

	const onSort = useCallback((key) => {
		setSort((previous) => ({ column: key, direction: nextSortDirection(previous.column, previous.direction, key) }));
	}, []);

	const sortedRows = useMemo(
		() =>
			sortRows(rows, {
				accessor: sort.column && accessors[sort.column],
				direction: sort.direction,
				tieBreaker: tieBreakerColumn && accessors[tieBreakerColumn],
			}),
		// `accessors` is intentionally not a dependency: callers build it inline,
		// so it's a new object every render, and the functions in it are pure
		// row readers that never change behaviour between renders.
		[rows, sort, tieBreakerColumn],
	);

	return { sortedRows, sortColumn: sort.column, direction: sort.direction, onSort };
}

export default useClientSort;
